/**
 * @file usePlatformEdgeConfig.ts
 * @description Адреса платформы для раздела «Магазины» (phase19.28 этап А;
 * ревью В1): базовый домен поддоменов, внешние IP сервера, цель CNAME и
 * путь к памятке NPM — с API (`GET /admin/platform/edge-config`), чтобы смена
 * IP провайдером не требовала новой сборки кабинета.
 *
 * Загрузка — один раз на раздел (общее обещание в модуле), до ответа и при
 * отказе (404, сеть) — запасные значения сборки из `platform-env.ts`.
 */

import { useEffect, useState } from "react";

import { platformEdge, type PlatformEdgeConfig } from "../../lib/api-platform-shops";
import { NPM_GUIDE_DOC, PLATFORM_BASE_DOMAIN, PLATFORM_PUBLIC_IP } from "../../lib/platform/platform-env";

/** Запасные значения — переменные сборки. */
export const FALLBACK_EDGE_CONFIG: PlatformEdgeConfig = {
  baseDomain: PLATFORM_BASE_DOMAIN,
  publicIps: [PLATFORM_PUBLIC_IP],
  cnameTarget: null,
  npmGuidePath: NPM_GUIDE_DOC,
};

let pending: Promise<PlatformEdgeConfig> | null = null;
let loaded: PlatformEdgeConfig | null = null;

/** Пустые поля ответа заменяются запасными — инструкция не теряет IP. */
function withFallback(config: PlatformEdgeConfig): PlatformEdgeConfig {
  return {
    baseDomain: config.baseDomain || FALLBACK_EDGE_CONFIG.baseDomain,
    publicIps: config.publicIps?.length ? config.publicIps : FALLBACK_EDGE_CONFIG.publicIps,
    cnameTarget: config.cnameTarget ?? null,
    npmGuidePath: config.npmGuidePath || FALLBACK_EDGE_CONFIG.npmGuidePath,
  };
}

function loadEdgeConfig(): Promise<PlatformEdgeConfig> {
  pending ??= platformEdge
    .config()
    .then(withFallback)
    .catch(() => FALLBACK_EDGE_CONFIG)
    .then((config) => (loaded = config));
  return pending;
}

/** Для тестов: забыть загруженные значения. */
export function resetEdgeConfigCache(): void {
  pending = null;
  loaded = null;
}

export function usePlatformEdgeConfig(): PlatformEdgeConfig {
  const [config, setConfig] = useState<PlatformEdgeConfig>(loaded ?? FALLBACK_EDGE_CONFIG);
  useEffect(() => {
    if (loaded) return;
    let alive = true;
    void loadEdgeConfig().then((next) => {
      if (alive) setConfig(next);
    });
    return () => {
      alive = false;
    };
  }, []);
  return loaded ?? config;
}
